// エージェントに渡す 8 ツールの定義と実装（plan.md §6.2）。
//
// 役割: DuckDB を単一空間エンジンとする薄いツール面を構成する。各ハンドラは duckdb/ と
//       data/layer-service の関数を呼ぶだけの薄いラッパーで、LLM へ返すのは要約に絞る
//       （全 GeoJSON は履歴に入れない＝コンテキスト肥大の抑制）。
// 関係: ToolRegistry に登録し runAgent から実行される。レイヤー生成/削除は App から注入される
//       コールバック（addLayerToMap / dropLayerFromMap）で地図描画に反映する。
//
// 設計: run_spatial_sql は layer_name 付きで SELECT を materialize するのが基本形。layer_name
//       なしの SELECT は確認用にプレビュー行を返す（影響行数/プレビュー, plan.md §6.2）。
import { ToolRegistry } from '../agent/tool-registry'
import { query } from '../duckdb/db'
import { listLayers } from '../duckdb/layer-registry'
import {
  addSqlLayer,
  importUrl,
  deleteLayer,
  describeLayer,
  loadRenderObject,
  importGeoJSON,
  computeChoroplethStyle,
} from '../data/layer-service'
import { downloadBoundaryGeoJSON, getBoundaryMetadata } from '../data/geoboundaries-client'

// 描画オブジェクトから LLM 向けの要約を作る（featureCollection 全体は返さない）。
function summarizeLayer(layer) {
  const feats = layer.featureCollection?.features ?? []
  const columns = feats[0] ? Object.keys(feats[0].properties ?? {}) : []
  const sample = feats.slice(0, 3).map((f) => f.properties)
  return {
    layerId: layer.layerId,
    displayName: layer.displayName,
    geomType: layer.geomType,
    featureCount: layer.featureCount,
    bounds: layer.bounds,
    columns,
    sample,
  }
}

// --- ツール定義（LLM へ渡す JSON スキーマ） ---

const RUN_SPATIAL_SQL = {
  name: 'run_spatial_sql',
  description:
    'DuckDB（spatial 拡張）で SQL を実行する。layer_name を指定すると SELECT 結果を新規レイヤーとして ' +
    'materialize し地図へ描画する（基本形。geom 列を必ず含めること）。layer_name を省略した場合は ' +
    '確認用に SELECT のプレビュー行を返す（このときは geom 列を直接 SELECT せず ST_AsText(geom) や ' +
    '属性・集計値を返すこと）。メートル/面積操作は必ず投影の三段構えで書く。',
  input_schema: {
    type: 'object',
    properties: {
      sql: { type: 'string', description: '実行する SELECT / WITH 文（末尾セミコロン不要）' },
      layer_name: {
        type: 'string',
        description: '新規レイヤー名。指定すると結果を materialize して地図に追加する。',
      },
      source_layers: {
        type: 'array',
        items: { type: 'string' },
        description: '生成元レイヤーの ID 配列（来歴記録用、任意）',
      },
    },
    required: ['sql'],
  },
}

const LIST_LAYERS = {
  name: 'list_layers',
  description: '既存レイヤーの一覧（ID・表示名・ジオメトリ型・地物数）をレジストリから返す。',
  input_schema: { type: 'object', properties: {} },
}

const DESCRIBE_LAYER = {
  name: 'describe_layer',
  description:
    '指定レイヤーの列構成・ジオメトリ型・範囲（bbox）・サンプル行を返す。SQL を書く前の確認に使う。',
  input_schema: {
    type: 'object',
    properties: { layer_id: { type: 'string', description: '対象レイヤーの ID' } },
    required: ['layer_id'],
  },
}

const IMPORT_DATA = {
  name: 'import_data',
  description:
    'URL から外部データ（GeoJSON / FlatGeobuf / GeoParquet）を取り込みレイヤー化する。' +
    'ローカルファイルは画面の取込 UI を使う。CRS が判別できない形式では crs を指定する（GeoJSON は不要）。',
  input_schema: {
    type: 'object',
    properties: {
      url: { type: 'string', description: '取得元 URL（CORS 許可が必要）' },
      crs: { type: 'string', description: '取込元の CRS（例: "EPSG:3857"）。GeoJSON は省略可。' },
    },
    required: ['url'],
  },
}

const GET_LAYER_GEOJSON = {
  name: 'get_layer_geojson',
  description: '指定レイヤーを地図に（再）描画する。地物数が多すぎる場合は描画せず警告を返す。',
  input_schema: {
    type: 'object',
    properties: { layer_id: { type: 'string', description: '対象レイヤーの ID' } },
    required: ['layer_id'],
  },
}

const GET_BOUNDARY_INFO = {
  name: 'get_boundary_info',
  description:
    'geoBoundaries APIで利用可能な行政界とライセンスを確認する。fetch_boundariesの前に使用する。',
  input_schema: {
    type: 'object',
    properties: {
      iso3: { type: 'string', description: 'ISO 3166-1 alpha-3国コード（例: JPN, USA）' },
      adm_level: {
        type: 'string',
        enum: ['ADM0', 'ADM1', 'ADM2', 'ADM3', 'ADM4', 'ADM5', 'ALL'],
        description: 'ADM0=国境、ADM1=州・都道府県、ADM2=市郡相当。省略時ALL。',
      },
    },
    required: ['iso3'],
  },
}

const FETCH_BOUNDARIES = {
  name: 'fetch_boundaries',
  description:
    'geoBoundariesから行政界GeoJSONを取得してレイヤー化する。通常はdetail=simplifiedを使用する。' +
    '取得前にget_boundary_infoで存在とライセンスを確認すること。',
  input_schema: {
    type: 'object',
    properties: {
      iso3: { type: 'string', description: 'ISO 3166-1 alpha-3国コード（例: JPN）' },
      adm_level: {
        type: 'string',
        enum: ['ADM0', 'ADM1', 'ADM2', 'ADM3', 'ADM4', 'ADM5'],
      },
      release_type: {
        type: 'string',
        enum: ['gbOpen', 'gbHumanitarian', 'gbAuthoritative'],
        description: '省略時はgbOpen',
      },
      detail: { type: 'string', enum: ['simplified', 'full'], description: '省略時はsimplified' },
      layer_name: { type: 'string', description: '表示名。省略時はgb_<ISO>_<ADM>' },
    },
    required: ['iso3', 'adm_level'],
  },
}

const SET_LAYER_STYLE = {
  name: 'set_layer_style',
  description:
    'レイヤーを属性値で塗り分ける（コロプレス）。fill_column の列で地図の塗り色を決め、凡例を' +
    'レイヤーパネルに表示する。数値列は段彩（連続/発散）、文字列列はカテゴリ配色に自動判定する。' +
    'clear=true で塗り分けを解除し単色へ戻す。列名は describe_layer で確認してから指定する。',
  input_schema: {
    type: 'object',
    properties: {
      layer_id: { type: 'string', description: '対象レイヤーの ID' },
      fill_column: { type: 'string', description: '塗り分けに使う属性列名' },
      method: {
        type: 'string',
        enum: ['quantile', 'equal_interval', 'categorical'],
        description:
          '分類方法。省略時は列型で自動（数値=quantile / 文字列=categorical）。' +
          'quantile=等量・equal_interval=等間隔（数値）、categorical=カテゴリ（名義）。',
      },
      classes: { type: 'integer', description: '数値段彩のクラス数（2〜9、既定 5）' },
      scheme: {
        type: 'string',
        description:
          'カラースキーム名。連続: YlOrRd/Blues/Greens/Viridis、発散: RdBu/RdYlGn、' +
          'カテゴリ: Set2/Tableau10。省略時は数値=YlOrRd・カテゴリ=Set2。',
      },
      opacity: { type: 'integer', description: '塗りの不透明度 0〜255（既定 180）' },
      clear: { type: 'boolean', description: 'true で塗り分けを解除し単色に戻す' },
    },
    required: ['layer_id'],
  },
}

const DROP_LAYER = {
  name: 'drop_layer',
  description: 'レイヤー（テーブルとレジストリ行）を削除し、地図からも取り除く。',
  input_schema: {
    type: 'object',
    properties: { layer_id: { type: 'string', description: '削除するレイヤーの ID' } },
    required: ['layer_id'],
  },
}

/**
 * 空間ツールのレジストリを生成する。
 *
 * deps:
 *   addLayerToMap(layerObject): 生成/再描画したレイヤーを地図（layer-store）へ反映する。
 *   dropLayerFromMap(layerId):  レイヤーを地図（layer-store）から取り除く。
 *   setLayerStyle(layerId, style): レイヤーの描画スタイル（コロプレス等）を差し替える（null で単色）。
 *   session: { originPrompt }   現在のユーザープロンプト（来歴 origin_prompt 記録用、可変）。
 */
export function createSpatialToolRegistry(deps) {
  const {
    addLayerToMap,
    dropLayerFromMap,
    setLayerStyle = () => {},
    session = {},
  } = deps

  return new ToolRegistry()
    .register(RUN_SPATIAL_SQL, async (input) => {
      if (input.layer_name) {
        // SELECT を新規レイヤーとして materialize し、地図へ描画する。
        const layer = await addSqlLayer(input.sql, {
          displayName: input.layer_name,
          baseName: input.layer_name,
          originPrompt: session.originPrompt ?? null,
          sourceLayers: input.source_layers ?? [],
        })
        addLayerToMap(layer)
        return summarizeLayer(layer)
      }
      // layer_name なし: 確認用にプレビューを返す。
      const rows = await query(input.sql)
      return { rowCount: rows.length, preview: rows.slice(0, 20) }
    })
    .register(LIST_LAYERS, async () => {
      const rows = await listLayers()
      return rows.map((r) => ({
        layerId: r.layer_id,
        displayName: r.display_name,
        geomType: r.geom_type,
        featureCount: Number(r.feature_count),
        sourceLayers: r.source_layers ?? [],
      }))
    })
    .register(DESCRIBE_LAYER, async (input) => describeLayer(input.layer_id))
    .register(IMPORT_DATA, async (input) => {
      const layer = await importUrl(input.url, { declaredCrs: input.crs })
      addLayerToMap(layer)
      return summarizeLayer(layer)
    })
    .register(GET_LAYER_GEOJSON, async (input) => {
      const { layer, truncated } = await loadRenderObject(input.layer_id)
      if (truncated) {
        return {
          layerId: input.layer_id,
          truncated: true,
          featureCount: layer.featureCount,
          message:
            '地物数が多く全量描画を中断しました。ST_Simplify や bbox フィルタ、属性列の削減で軽くしてください。',
        }
      }
      addLayerToMap(layer)
      return { layerId: input.layer_id, featureCount: layer.featureCount, bounds: layer.bounds }
    })
    .register(GET_BOUNDARY_INFO, async (input, context) => {
      const entries = await getBoundaryMetadata({
        iso3: input.iso3,
        admLevel: input.adm_level ?? 'ALL',
        signal: context?.signal,
      })
      return {
        boundaries: entries.map((entry) => ({
          iso3: entry.boundaryISO,
          name: entry.boundaryName,
          admLevel: entry.boundaryType,
          year: entry.boundaryYearRepresented,
          featureCount: entry.admUnitCount,
          meanVertices: entry.meanVertices,
          license: entry.boundaryLicense,
          source: entry.boundarySource,
        })),
      }
    })
    .register(FETCH_BOUNDARIES, async (input, context) => {
      const [meta] = await getBoundaryMetadata({
        iso3: input.iso3,
        admLevel: input.adm_level,
        releaseType: input.release_type ?? 'gbOpen',
        signal: context?.signal,
      })
      const useFull = input.detail === 'full'
      const url = useFull ? meta.gjDownloadURL : meta.simplifiedGeometryGeoJSON
      if (!url) throw new Error('geoBoundariesのメタデータにダウンロードURLがありません')
      const geojson = await downloadBoundaryGeoJSON(url, { signal: context?.signal })
      const displayName = input.layer_name || `gb_${meta.boundaryISO}_${meta.boundaryType}`
      const layer = await importGeoJSON(geojson, { displayName })
      addLayerToMap(layer)
      return {
        ...summarizeLayer(layer),
        source: {
          name: meta.boundaryName,
          year: meta.boundaryYearRepresented,
          license: meta.boundaryLicense,
          licenseSource: meta.licenseSource,
          boundarySource: meta.boundarySource,
          detail: useFull ? 'full' : 'simplified',
        },
      }
    })
    .register(SET_LAYER_STYLE, async (input) => {
      if (input.clear) {
        setLayerStyle(input.layer_id, null)
        return { layerId: input.layer_id, cleared: true }
      }
      const style = await computeChoroplethStyle(input.layer_id, {
        column: input.fill_column,
        method: input.method,
        classes: input.classes,
        scheme: input.scheme,
        opacity: input.opacity,
      })
      setLayerStyle(input.layer_id, style)
      return {
        layerId: input.layer_id,
        column: style.column,
        mode: style.mode,
        scheme: style.scheme,
        classes: style.mode === 'numeric' ? style.breaks.length + 1 : style.categories.length,
        legend: style.legend.map((e) => e.label),
      }
    })
    .register(DROP_LAYER, async (input) => {
      await deleteLayer(input.layer_id)
      dropLayerFromMap(input.layer_id)
      return { dropped: input.layer_id }
    })
}
